import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Box, Card, CardContent, Stack, Typography, TextField, Switch, Button, Chip,
  InputAdornment, Divider,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import api from "../api";
import { MONO, tokens } from "../theme";
import { PageHeader, Eyebrow, StatusDot } from "../components/ui";

// Working form value for a setting: the string in a number box, or a bool.
const formValue = (s) => (s.type === "bool" ? s.value : String(s.value));

function fieldError(s, v) {
  if (s.type === "bool") return null;
  if (v === "" || v === null || v === undefined) return "Required";
  const n = Number(v);
  if (!Number.isInteger(n)) return "Whole number";
  if (s.min != null && n < s.min) return `Min ${s.min}`;
  if (s.max != null && n > s.max) return `Max ${s.max}`;
  return null;
}

export default function Settings() {
  const [spec, setSpec] = useState(null);       // [{key,section,type,...,value,default}]
  const [env, setEnv] = useState(null);
  const [values, setValues] = useState({});      // key -> form value
  const [saving, setSaving] = useState(false);

  const seed = useCallback((settings) => {
    setValues(Object.fromEntries(settings.map((s) => [s.key, formValue(s)])));
  }, []);

  const load = useCallback(() => {
    api.get("/api/settings", { silent: true })
      .then((d) => { setSpec(d.settings); setEnv(d.environment); seed(d.settings); })
      .catch(() => {});
  }, [seed]);

  useEffect(() => { load(); }, [load]);

  // Refresh the read-only environment (workers online) on an interval.
  useEffect(() => {
    const t = setInterval(() => {
      api.get("/api/settings", { silent: true }).then((d) => setEnv(d.environment)).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, []);

  const groups = useMemo(() => {
    if (!spec) return [];
    const order = [];
    const by = {};
    for (const s of spec) {
      if (!by[s.section]) { by[s.section] = []; order.push(s.section); }
      by[s.section].push(s);
    }
    return order.map((section) => ({ section, items: by[section] }));
  }, [spec]);

  const dirtyKeys = useMemo(
    () => (spec || []).filter((s) => !s.locked && values[s.key] !== formValue(s)).map((s) => s.key),
    [spec, values],
  );
  const hasError = useMemo(
    () => (spec || []).some((s) => !s.locked && fieldError(s, values[s.key])),
    [spec, values],
  );
  const dirty = dirtyKeys.length > 0;

  const save = async () => {
    if (!dirty || hasError) return;
    const changes = {};
    for (const key of dirtyKeys) {
      const s = spec.find((x) => x.key === key);
      changes[key] = s.type === "bool" ? values[key] : Number(values[key]);
    }
    setSaving(true);
    try {
      const d = await api.patch("/api/settings", { settings: changes });
      setSpec(d.settings); setEnv(d.environment); seed(d.settings);
      const restarts = dirtyKeys.filter((k) => spec.find((x) => x.key === k)?.restart_required);
      toast.success(
        restarts.length
          ? "Settings saved — restart the control center to apply concurrency changes."
          : "Settings saved.",
      );
    } catch {
      /* api wrapper toasts the error */
    } finally {
      setSaving(false);
    }
  };

  if (!spec) return null;

  return (
    <Box>
      <PageHeader
        eyebrow="Configuration"
        title="Settings"
        subtitle="Tune the scheduler, warm-model pool and cluster behaviour. Changes are saved to the platform database, take effect immediately, and reach worker nodes on their next heartbeat."
        actions={
          <>
            <Button variant="outlined" size="small" disabled={!dirty || saving} onClick={() => seed(spec)}>
              Discard
            </Button>
            <Button variant="contained" size="small" disabled={!dirty || hasError || saving} onClick={save}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </>
        }
      />

      <Stack spacing={2.5} sx={{ maxWidth: 860 }}>
        {groups.map(({ section, items }) => (
          <Card key={section}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ px: 3, pt: 2.25, pb: 1.5 }}>
                <Eyebrow>{section}</Eyebrow>
              </Box>
              <Divider />
              {items.map((s, i) => (
                <SettingRow
                  key={s.key}
                  s={s}
                  value={values[s.key]}
                  error={fieldError(s, values[s.key])}
                  dirty={values[s.key] !== formValue(s)}
                  disabled={saving}
                  last={i === items.length - 1}
                  onChange={(v) => setValues((prev) => ({ ...prev, [s.key]: v }))}
                />
              ))}
            </CardContent>
          </Card>
        ))}

        {env && <EnvironmentCard env={env} />}
      </Stack>
    </Box>
  );
}

function SettingRow({ s, value, error, dirty, disabled, last, onChange }) {
  const ro = disabled || s.locked;   // read-only: saving, or pinned by the environment
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 3,
        py: 2,
        flexWrap: "wrap",
        borderBottom: last ? "none" : `1px solid ${tokens.line}`,
        ...(dirty && { background: "rgba(46,139,230,.035)" }),
      }}
    >
      <Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontWeight: 600, fontSize: ".92rem" }}>{s.label}</Typography>
          {s.locked && (
            <Chip
              size="small"
              variant="outlined"
              icon={<LockOutlinedIcon sx={{ fontSize: 13 }} />}
              label={`Set by ${s.key}`}
              sx={{ color: tokens.slate, borderColor: tokens.line, "& .MuiChip-icon": { color: tokens.slate } }}
            />
          )}
          {s.restart_required && !s.locked && (
            <Chip
              size="small"
              variant="outlined"
              icon={<RestartAltIcon sx={{ fontSize: 13 }} />}
              label="Restart required"
              sx={{ color: tokens.warn, borderColor: "#f0d9b5", "& .MuiChip-icon": { color: tokens.warn } }}
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4, pr: 2 }}>
          {s.help}
        </Typography>
      </Box>

      <Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 1 }}>
        {s.type === "bool" ? (
          <Switch checked={!!value} disabled={ro} onChange={(e) => onChange(e.target.checked)} />
        ) : (
          <TextField
            type="number"
            size="small"
            value={value}
            disabled={ro}
            onChange={(e) => onChange(e.target.value)}
            error={!!error}
            helperText={error || (s.locked ? "From environment" : `Default ${s.default}`)}
            inputProps={{ min: s.min, max: s.max, style: { fontFamily: MONO, width: 92, textAlign: "right" } }}
            InputProps={s.unit ? {
              endAdornment: <InputAdornment position="end"><Typography sx={{ fontFamily: MONO, fontSize: ".72rem", color: "text.secondary" }}>{s.unit}</Typography></InputAdornment>,
            } : undefined}
            FormHelperTextProps={{ sx: { fontFamily: MONO, fontSize: ".66rem", mx: 0, textAlign: "right" } }}
          />
        )}
      </Box>
    </Box>
  );
}

function EnvironmentCard({ env }) {
  const rows = [
    ["Mode", env.mode],
    ["Bind", `${env.host}:${env.port}`],
    ["Node ID", env.node_id],
    ["Server workers", env.server_workers],
    ["Database", env.db_path],
    ["Venvs dir", env.venvs_dir],
    ...(env.mock_gpus ? [["Mock GPUs", env.mock_gpus]] : []),
  ];
  const flags = [
    ["Clustering", env.clustering_enabled],
    ["Fernet key", env.fernet_key_set],
    ["Auth secret via env", env.auth_secret_from_env],
  ];
  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2, mb: 0.5, flexWrap: "wrap" }}>
          <Eyebrow>Environment</Eyebrow>
          <Typography variant="caption" color="text.secondary">
            Set at deploy time (env vars) — read-only here.
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 620 }}>
          Topology, paths and secrets are fixed at startup or are per-process cluster identity, so
          they're configured through the environment rather than the dashboard.
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 2 }}>
          {rows.map(([label, value]) => (
            <Box key={label} sx={{ minWidth: 0 }}>
              <Eyebrow sx={{ mb: 0.3 }}>{label}</Eyebrow>
              <Typography sx={{ fontFamily: MONO, fontSize: ".82rem", color: "text.primary", overflowWrap: "anywhere" }}>
                {String(value)}
              </Typography>
            </Box>
          ))}
          {flags.map(([label, on]) => (
            <Box key={label} sx={{ minWidth: 0 }}>
              <Eyebrow sx={{ mb: 0.3 }}>{label}</Eyebrow>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <StatusDot color={on ? tokens.ok : tokens.slate} size={7} />
                <Typography sx={{ fontFamily: MONO, fontSize: ".82rem" }}>
                  {label === "Clustering" ? (on ? `on · ${env.workers_online} online` : "off") : on ? "set" : "not set"}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}
